import { createSlice } from '@reduxjs/toolkit'
import { sortByProp } from '../../../../../../helpers/common'
import { register } from '../../../../../../helpers/reducerRegistry'
import { SORT_DIRECTION } from '../../../../../../utils/common'
import { handleData, handleYearHeader } from '../../helpers'
import { getNominalGDP, getRealGDP } from './thunk'

const slice = createSlice({
  name: 'companyAnalysis/industryOverview/nominalGDP',
  initialState: {
    year: undefined,
    isLoading: {
      REAL_GDP: true,
      NOMINAL_GDP: true,
    },
    realData: {},
    realIds: [],
    realYears: [],
    realHeaders: [],
    real: [],

    nominalData: {},
    nominalIds: [],
    nominalYears: [],
    nominalHeaders: [],
    nominal: [],

    sortTable: {
      sortBy: '',
      sortDirection: '',
    },
  },
  reducers: {
    resetStore: (state) => {
      state.isLoading.REAL_GDP = true
      state.isLoading.NOMINAL_GDP = true
      state.realData = {}
      state.realIds = []
      state.realYears = []
      state.realHeaders = []
      state.real = []
      state.year = undefined

      state.nominalData = {}
      state.nominalIds = []
      state.nominalYears = []
      state.nominalHeaders = []
      state.nominal = []

      state.sortTable = {
        sortBy: '',
        sortDirection: '',
      }
    },
    changeYear: (state, action) => {
      state.year = action.payload
    },
    sortReal: (state, action) => {
      state.sortTable = action.payload
      if (action.payload.sortDirection) {
        state.realIds = sortByProp(
          Object.values(state.realData),
          action.payload.sortBy,
          action.payload.sortDirection === SORT_DIRECTION.DESC,
          false,
        ).map((item) => item.id)
      } else {
        state.realIds = [...new Set(state.real.map((item) => item.vsicId))]
      }
    },
    sortNominal: (state, action) => {
      state.sortTable = action.payload
      if (action.payload.sortDirection) {
        state.nominalIds = sortByProp(
          Object.values(state.nominalData),
          action.payload.sortBy,
          action.payload.sortDirection === SORT_DIRECTION.DESC,
          false,
        ).map((item) => item.id)
      } else {
        state.nominalIds = [
          ...new Set(state.nominal.map((item) => item.vsicId)),
        ]
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRealGDP.pending, (state) => {
      state.isLoading.REAL_GDP = true
      state.realData = {}
      state.realIds = []
      state.realYears = []
      state.realHeaders = []
      state.real = []
      state.year = undefined
      state.sortTable = {
        sortBy: '',
        sortDirection: '',
      }
    })
    builder.addCase(getRealGDP.fulfilled, (state, { payload = [] }) => {
      state.isLoading.REAL_GDP = false
      state.realIds = [...new Set(payload.map((item) => item.vsicId))]
      state.realYears = [...new Set(payload.map((item) => item.year))]
      state.realHeaders = handleYearHeader(state.realYears)
      state.realData = handleData(payload, state.realIds, state.realYears)
      state.real = payload
      state.year = state.realYears.length ? state.realYears[0] : undefined
    })
    builder.addCase(getRealGDP.rejected, (state, action) => {
      state.isLoading.REAL_GDP = action.payload.isLoading
    })

    builder.addCase(getNominalGDP.pending, (state) => {
      state.isLoading.NOMINAL_GDP = true
      state.year = undefined
      state.nominalData = {}
      state.nominalIds = []
      state.nominalYears = []
      state.nominalHeaders = []
      state.nominal = []
    })
    builder.addCase(getNominalGDP.fulfilled, (state, { payload = [] }) => {
      state.isLoading.NOMINAL_GDP = false
      state.nominalIds = [...new Set(payload.map((item) => item.vsicId))]
      state.nominalYears = [...new Set(payload.map((item) => item.year))]
      state.nominalHeaders = handleYearHeader(state.nominalYears)
      state.nominalData = handleData(
        payload,
        state.nominalIds,
        state.nominalYears,
      )
      state.nominal = payload
      state.year = state.nominalYears.length ? state.nominalYears[0] : undefined
    })
    builder.addCase(getNominalGDP.rejected, (state, action) => {
      state.isLoading.NOMINAL_GDP = action.payload.isLoading
    })
  },
})

export const selectNominalYears = (state) => state[slice.name].nominalYears
export const selectNominalItem = (rowId, colId) => (state) =>
  state[slice.name].nominalData[rowId][colId]
export const selectNominalData = (state) => state[slice.name].nominalData
export const selectNominalHeaders = (state) => state[slice.name].nominalHeaders
export const selectNominalIds = (state) => state[slice.name].nominalIds
export const selectNominal = (state) => state[slice.name].nominal
export const selectIsLoadingNominal = (state) =>
  state[slice.name].isLoading.NOMINAL_GDP

export const selectRealYears = (state) => state[slice.name].realYears
export const selectRealItem = (rowId, colId) => (state) =>
  state[slice.name].realData[rowId][colId]
export const selectRealData = (state) => state[slice.name].realData
export const selectRealHeaders = (state) => state[slice.name].realHeaders
export const selectRealIds = (state) => state[slice.name].realIds
export const selectReal = (state) => state[slice.name].real
export const selectIsLoadingReal = (state) =>
  state[slice.name].isLoading.REAL_GDP

export const selectYear = (state) => state[slice.name].year
export const selectSortTable = (state) => state[slice.name].sortTable

export const { resetStore, changeYear, sortReal, sortNominal } = slice.actions

register(slice.name, slice.reducer)
